import type { ComponentProps } from 'react';
import { lazyQuiet } from '@/routing/lazyQuiet';
import type { RefusedTouchHint as LoadedHint } from './RefusedTouchHint';

const Hint = lazyQuiet<
  typeof import('./RefusedTouchHint'),
  NonNullable<ComponentProps<typeof LoadedHint>>
>(
  () => import('./RefusedTouchHint'),
  (module) => module.RefusedTouchHint,
);

/*
 * Until the hint's chunk is in, the last touch on the page is noted here (one listener, in the
 * entry), and the hint shows that tap's reason as it mounts: a refused control tapped in the first
 * moment is not lost.
 */
let earlyTap: Element | null = null;
function noteTap(event: Event): void {
  if ((event as PointerEvent).pointerType !== 'touch') return;
  earlyTap = event.target instanceof Element ? event.target : null;
}
// A press after the tap ends a hint the tap would have shown, as it does once the hint is in.
function notePress(): void {
  earlyTap = null;
}
if (typeof document !== 'undefined') {
  document.addEventListener('pointerup', noteTap, true);
  document.addEventListener('pointerdown', notePress, true);
}
function takeEarlyTap(): Element | null {
  document.removeEventListener('pointerup', noteTap, true);
  document.removeEventListener('pointerdown', notePress, true);
  const tap = earlyTap;
  earlyTap = null;
  return tap;
}

/**
 * F-0324's touch and keyboard hint, mounted once by `App`, in its own chunk (the entry budget,
 * 2026-10-30: 4.4 KB of the launch entry). It listens on the document from the moment its chunk is
 * in, a beat after the first screen; a tap before that is shown as it mounts (`takeEarlyTap`).
 * Offline it is not drawn, and a refused control still says its reason to a screen reader
 * (`RefusalReasons`) and on hover (its `title`).
 */
export function RefusedTouchHint() {
  return <Hint tappedBefore={takeEarlyTap} />;
}
